import { MouseEvent, useCallback, useEffect, useState } from 'react';
import useTimeout from '@smart-tv/hooks/use-timeout/use-timeout';

import { PLAYER_CONTROLS_INTERACTIVE_SELECTOR } from '../player-helpers';

interface Params {
  enabled: boolean;
  timeout: number;
  onHide: () => void;
}

export const useAutoHide = (params: Params) => {
  const { enabled, timeout, onHide } = params;

  const [autoHideTimeout, setAutoHideTimeout] = useState<number>();
  const [keyPress, setKeyPress] = useState(false);
  const [isHoveringInteractiveControl, setIsHoveringInteractiveControl] = useState(false);

  const cancel = useCallback(() => {
    setAutoHideTimeout(undefined);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setAutoHideTimeout(undefined);
      setKeyPress(false);
      setIsHoveringInteractiveControl(false);
      return;
    }

    if (!autoHideTimeout && !keyPress && !isHoveringInteractiveControl) {
      setAutoHideTimeout(timeout);
    }
  }, [enabled, autoHideTimeout, keyPress, isHoveringInteractiveControl, timeout]);

  useTimeout(() => {
    setAutoHideTimeout(undefined);
    onHide();
  }, autoHideTimeout);

  const handleKeyDown = useCallback(() => {
    if (!keyPress) {
      setAutoHideTimeout(undefined);
      setKeyPress(true);
    }
  }, [keyPress]);

  const handleKeyUp = useCallback(() => {
    setKeyPress(false);
  }, []);

  const handleMouseOver = useCallback((event: MouseEvent<HTMLElement>) => {
    const isInteractive = !!(event.target as HTMLElement).closest?.(
      PLAYER_CONTROLS_INTERACTIVE_SELECTOR
    );

    if (isInteractive) {
      setAutoHideTimeout(undefined);
    }

    setIsHoveringInteractiveControl(isInteractive);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHoveringInteractiveControl(false);
  }, []);

  return {
    handleKeyDown,
    handleKeyUp,
    handleMouseOver,
    handleMouseLeave,
    cancel
  };
};

export default useAutoHide;
